<template>
  <v-container fluid class="pa-0">
    <v-row no-gutters>
      <!-- Mapa -->
      <v-col cols="12" md="6" class="pa-2" style="height: calc(100vh - 80px)">
        <div id="map" style="height: 100%; width: 100%"></div>
      </v-col>

      <!-- Tabla -->
      <v-col cols="12" md="6" class="pa-2" style="height: calc(100vh - 80px)">
        <div class="d-flex flex-column" style="height: 100%">
          <!-- Filtros -->
          <div class="search-container">
            <v-text-field
              v-model="searchApodo"
              label="Buscar Apodo"
              dense
              outlined
              hide-details
            />
            <v-select
              v-model="searchTipo"
              :items="['iBeacon', 'SmartBle', 'Reader']"
              label="Filtrar Tipo"
              dense
              outlined
              clearable
              hide-details
            />
            <v-btn
              color="primary"
              dark
              small
              @click="clearFilters"
              :disabled="!hasFilters"
            >
              <v-icon left>mdi-filter-remove</v-icon> Limpiar
            </v-btn>
          </div>

          <!-- Tabla -->
          <v-data-table
            :headers="headers"
            :items="filteredVisibleDevices"
            :items-per-page="itemsPerPage"
            :page="page"
            hide-default-footer
            class="flex-grow-1"
          >
            <template #top>
              <v-toolbar flat>
                <v-toolbar-title>Inventario de Dispositivos</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  dark
                  @click="reloadData"
                  small
                  style="margin-right: 8px"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Recargar
                </v-btn>
              </v-toolbar>
            </template>
            <!-- Apodo chip clickable -->
            <template v-slot:[`item.Apodo`]="{ item }">
              <v-chip
                small
                color="primary"
                text-color="white"
                @click="goToInventory(item.Apodo)"
                style="cursor: pointer"
              >
                <v-icon start>mdi-cellphone</v-icon> {{ item.Apodo }}
              </v-chip>
            </template>

            <!-- Tipo Dispositivo chip -->
            <template v-slot:[`item.TipoDispositivo`]="{ item }">
              <v-chip :color="getTipoColor(item.TipoDispositivo)" dark small>
                <v-icon start size="16">{{
                  getTipoIcon(item.TipoDispositivo)
                }}</v-icon>
                {{ item.TipoDispositivo }}
              </v-chip>
            </template>

            <!-- Último Registro -->
            <template v-slot:[`item.UltimoRegistro`]="{ item }">
              <span>
                {{ formatDate(item.UltimoRegistro) }}
              </span>
            </template>

            <!-- Botón Detalles -->
            <template v-slot:[`item.detalles`]="{ item }">
              <v-btn icon @click="openDetails(item)">
                <v-icon color="info">mdi-information-outline</v-icon>
              </v-btn>
            </template>

            <!-- Footer -->
            <template #bottom>
              <div class="d-flex justify-space-between align-center pa-2">
                <div class="d-flex align-center">
                  <v-pagination
                    v-model="page"
                    :length="
                      Math.ceil(filteredVisibleDevices.length / itemsPerPage)
                    "
                    total-visible="3"
                    class="mr-4"
                  />
                  <span>Total: {{ filteredVisibleDevices.length }}</span>
                </div>
                <div class="d-flex">
                  <v-btn color="success" dark small @click="exportExcel">
                    <v-icon left>mdi-file-excel</v-icon> Exportar Excel
                  </v-btn>
                  <v-btn color="red" dark small class="ml-2" @click="exportPDF">
                    <v-icon left>mdi-file-pdf-box</v-icon> Exportar PDF
                  </v-btn>
                </div>
              </div>
            </template>
          </v-data-table>
          <v-alert
            v-if="filteredVisibleDevices.length === 0"
            type="info"
            border="left"
            colored-border
            elevation="2"
            class="ma-4"
          >
            <v-icon left>mdi-map-search</v-icon>
            No hay dispositivos visibles en esta zona del mapa.
          </v-alert>
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="detailsDialog" max-width="500px" v-if="selectedDevice">
      <v-card>
        <v-card-title>Detalles del dispositivo</v-card-title>
        <v-card-text>
          <v-list dense>
            <!-- Tipo Dispositivo como chip -->
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Tipo Dispositivo:</strong>
                  <v-chip
                    small
                    :color="getTipoColor(selectedDevice.TipoDispositivo)"
                    dark
                    class="ml-2"
                  >
                    <v-icon start size="16">
                      {{ getTipoIcon(selectedDevice.TipoDispositivo) }}
                    </v-icon>
                    {{ selectedDevice.TipoDispositivo }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Último Registro con icono de alerta si es antiguo -->
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <v-icon
                    v-if="isOldUpdate(selectedDevice.UltimoRegistro)"
                    color="red"
                    class="mr-1"
                    size="16"
                  >
                    mdi-alert-circle
                  </v-icon>
                  <v-icon v-else color="green" class="mr-1" size="16">
                    mdi-clock-outline
                  </v-icon>
                  <strong>Último Registro:</strong>
                  <span class="ml-1">{{
                    formatDate(selectedDevice.UltimoRegistro)
                  }}</span>
                </v-list-item-title>
                <v-list-item-subtitle class="text-grey">
                  {{ getRelativeTime(selectedDevice.UltimoRegistro) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Visto por:</strong>
                  {{ selectedDevice.ApodoLector || "Sin datos" }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Posible Estado como chip -->
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Posible Estado:</strong>
                  <v-chip
                    small
                    :color="getEstadoColor(selectedDevice.PosibleEstado)"
                    dark
                    class="ml-2"
                  >
                    <v-icon start size="16">
                      {{ getEstadoIcon(selectedDevice.PosibleEstado) }}
                    </v-icon>
                    {{ selectedDevice.PosibleEstado }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Estado (Impacto / En movimiento / Parado) -->
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <strong>Movimiento:</strong>
                  <span class="ml-2 d-flex align-center">
                    <v-icon
                      v-if="selectedDevice.Estado === 'Impacto'"
                      color="red"
                      class="mr-1"
                      size="16"
                      >mdi-alert</v-icon
                    >
                    <v-icon
                      v-else-if="selectedDevice.Estado === 'En movimiento'"
                      color="blue"
                      class="mr-1"
                      size="16"
                      >mdi-run</v-icon
                    >
                    <v-icon
                      v-else-if="selectedDevice.Estado === 'Parado'"
                      color="grey"
                      class="mr-1"
                      size="16"
                      >mdi-stop-circle</v-icon
                    >
                    <span>
                      {{
                        selectedDevice.Estado === "Impacto"
                          ? "Impacto"
                          : selectedDevice.Estado === "En movimiento"
                          ? "En movimiento"
                          : selectedDevice.Estado === "Parado"
                          ? "Parado"
                          : selectedDevice.Estado || "—"
                      }}
                    </span>
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="detailsDialog = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="showIncidenciasPopup"
      timeout="-1"
      location="top right"
      elevation="10"
      color="info"
      class="rounded-lg pa-4"
      transition="slide-x-reverse-transition"
    >
      <div class="d-flex flex-column">
        <div class="mb-2">
          <strong>¡Aviso de Incidencias!</strong><br />
          Ha habido {{ totalIncidencias }} incidencias en
          {{ contenedoresAfectados }} contenedores diferentes.
        </div>
        <v-btn color="primary" small @click="goToIncidencias">
          Ver Incidencias
        </v-btn>
      </div>

      <template #actions>
        <v-btn icon @click="closeIncidenciasPopup">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed, watch, inject } from "vue";
import { usePocStore } from "@/stores/pocStore";
import { useRouter } from "vue-router";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet/dist/leaflet.css";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import debounce from "lodash/debounce";
import api from "@/services/api";

const hasSeenIncidenciasPopup = inject("hasSeenIncidenciasPopup");
const showIncidenciasPopup = ref(false);
const totalIncidencias = ref(0);
const contenedoresAfectados = ref(0);

const pocStore = usePocStore();
const router = useRouter();

const searchApodo = ref("");
const searchTipo = ref("");
const page = ref(1);
const itemsPerPage = ref(10);

const map = ref(null);
const markersLayer = ref(null);
const clusterGroup = ref(null);

const detailsDialog = ref(false);
const selectedDevice = ref(null);

const debouncedUpdateMarkers = debounce(updateMarkers, 300);

dayjs.extend(relativeTime);

// Cabeceras
const headers = [
  { title: "Apodo", value: "Apodo", sortable: true },
  { title: "Tipo", value: "TipoDispositivo", sortable: true },
  { title: "Último Registro", value: "UltimoRegistro", sortable: true },
  { title: "", value: "detalles", sortable: false },
];

// Computed
const hasFilters = computed(() => searchApodo.value || searchTipo.value);

const visibleDevices = computed(() => {
  if (!map.value) return [];
  const bounds = map.value.getBounds();
  return pocStore.data.filter((d) => {
    if (!d.Lat || !d.Lon) return false;
    const latlng = L.latLng(d.Lat, d.Lon);
    return bounds.contains(latlng);
  });
});

const filteredVisibleDevices = computed(() => {
  return visibleDevices.value.filter((item) => {
    const matchApodo = item.Apodo?.toLowerCase().includes(
      searchApodo.value.toLowerCase()
    );
    const matchTipo = searchTipo.value
      ? item.TipoDispositivo === searchTipo.value
      : true;
    return matchApodo && matchTipo;
  });
});

function getEstadoColor(estado) {
  switch (estado) {
    case "Ubicado":
      return "green";
    case "Posible tránsito":
      return "orange";
    case "Posible extravío":
      return "red";
    case "Necesita revisar":
      return "red lighten-2";
    case "Operativo":
      return "blue";
    case "Sin registro":
      return "grey";
    default:
      return "grey";
  }
}

function getEstadoIcon(estado) {
  switch (estado) {
    case "Ubicado":
      return "mdi-check-circle";
    case "Posible tránsito":
      return "mdi-map-marker-path";
    case "Posible extravío":
      return "mdi-alert-circle";
    case "Necesita revisar":
      return "mdi-wrench";
    case "Operativo":
      return "mdi-power-plug";
    case "Sin registro":
      return "mdi-clock-off";
    default:
      return "mdi-help-circle";
  }
}

// Comprobar si el último registro es antiguo
function isOldUpdate(ultimoRegistro) {
  if (!ultimoRegistro || ultimoRegistro === "Sin determinar") return true;
  const now = dayjs();
  const lastUpdate = dayjs(ultimoRegistro);
  return now.diff(lastUpdate, "minute") > 300; // Más de 60 minutos
}

// Iconos
function crearIcono(url) {
  return new L.Icon({
    iconUrl: url,
    iconSize: [36, 36], // un poco más grandes que antes
    iconAnchor: [18, 36], // ancla en la punta inferior
    popupAnchor: [0, -36], // el popup justo encima
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png", // Sombra clásica
    shadowSize: [41, 41], // tamaño sombra
    shadowAnchor: [13, 41], // dónde ancla la sombra
  });
}

// Definición de iconos para cada tipo de dispositivo
const iconosPorTipo = {
  Reader: crearIcono(
    "https://cdn.jsdelivr.net/npm/@mdi/svg/svg/access-point-network.svg"
  ),
  iBeacon: crearIcono(
    "https://cdn.jsdelivr.net/npm/@mdi/svg/svg/bluetooth.svg"
  ),
  SmartBle: crearIcono(
    "https://cdn.jsdelivr.net/npm/@mdi/svg/svg/bluetooth-connect.svg"
  ),
  Default: crearIcono(
    "https://cdn.jsdelivr.net/npm/@mdi/svg/svg/help-circle.svg"
  ),
};

// Limpiar offset de fecha
function stripOffset(value) {
  // elimina sufijo +HH:MM, -HH:MM o Z
  return value ? value.replace(/([+-]\d{2}:\d{2}|Z)$/, "") : value;
}

// Formatear fecha
function formatDate(date) {
  if (!date || date === "Sin determinar") return "Sin datos";
  const clean = stripOffset(date);
  const d = dayjs(clean);
  return d.isValid() ? d.format("DD/MM/YYYY HH:mm:ss") : "Sin datos";
}

function getTipoColor(tipo) {
  if (tipo === "Reader") return "blue";
  if (tipo === "iBeacon") return "green";
  if (tipo === "SmartBle") return "orange";
  return "grey";
}

function getTipoIcon(tipo) {
  if (tipo === "Reader") return "mdi-access-point-network";
  if (tipo === "iBeacon") return "mdi-bluetooth";
  if (tipo === "SmartBle") return "mdi-bluetooth-connect";
  return "mdi-help-circle";
}

function goToInventory(apodo) {
  router.push({ name: "PocStatus", query: { apodo } });
}

function openDetails(item) {
  selectedDevice.value = item;
  detailsDialog.value = true;
}

function clearFilters() {
  searchApodo.value = "";
  searchTipo.value = "";
  page.value = 1;
}

function reloadData() {
  pocStore.fetchPocData();
}

// Obtener tiempo relativo
function getRelativeTime(value) {
  if (!value || value === "Sin determinar") return "Sin datos";
  const clean = stripOffset(value);
  const d = dayjs(clean);
  return d.isValid() ? d.fromNow() : "Sin datos";
}

// Exportar Excel
async function exportExcel() {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Dispositivos Visibles");
  worksheet.columns = headers.map((h) => ({
    header: h.title,
    key: h.value,
    width: 20,
  }));

  filteredVisibleDevices.value.forEach((item) => {
    worksheet.addRow({
      Apodo: item.Apodo,
      TipoDispositivo: item.TipoDispositivo,
      UltimoRegistro: formatDate(item.UltimoRegistro),
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), "dispositivos_visibles.xlsx");
}

// Exportar PDF
async function exportPDF() {
  const doc = new jsPDF("p", "mm", "a4");

  doc.setFontSize(20);
  doc.text("Dispositivos visibles en mapa", 14, 20);

  doc.setFontSize(10);
  doc.text(`Exportado el: ${dayjs().format("DD/MM/YYYY HH:mm:ss")}`, 14, 27);

  const columns = [
    { header: "Apodo", dataKey: "Apodo" },
    { header: "Tipo", dataKey: "TipoDispositivo" },
    { header: "Último Registro", dataKey: "UltimoRegistro" },
  ];

  const rows = filteredVisibleDevices.value.map((item) => ({
    Apodo: item.Apodo,
    TipoDispositivo: item.TipoDispositivo,
    UltimoRegistro: formatDate(item.UltimoRegistro),
  }));

  autoTable(doc, {
    startY: 35,
    head: [columns.map((c) => c.header)],
    body: rows.map((row) => columns.map((c) => row[c.dataKey])),
    theme: "striped",
    headStyles: { fillColor: [22, 160, 133] },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    styles: { fontSize: 8, cellPadding: 3 },
    margin: { left: 10, right: 10 },
    didDrawPage: () => {
      const pageSize = doc.internal.pageSize;
      doc.setFontSize(8);
      doc.text(
        `Página ${doc.internal.getNumberOfPages()}`,
        pageSize.width / 2,
        pageSize.height - 5,
        { align: "center" }
      );
    },
  });

  doc.save("dispositivos_visibles.pdf");
}

onMounted(async () => {
  await pocStore.fetchPocData();

  map.value = L.map("map").setView([42.8467, -2.6727], 14); // Vitoria-Gasteiz

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: "&copy; OpenStreetMap contributors",
  }).addTo(map.value);

  map.value.on("zoomend", () => {
    reloadData();
    debouncedUpdateMarkers();
  });

  map.value.on("moveend", () => {
    reloadData();
    debouncedUpdateMarkers();
  });

  clusterGroup.value = L.markerClusterGroup();
  markersLayer.value = L.layerGroup().addTo(map.value);
  clusterGroup.value.addTo(map.value);

  updateMarkers();

  if (!hasSeenIncidenciasPopup.value) {
    try {
      const resumen = await api.getResumenIncidencias();
      totalIncidencias.value = resumen.totalIncidencias;
      contenedoresAfectados.value = resumen.contenedoresUnicos;

      if (totalIncidencias.value > 0) {
        showIncidenciasPopup.value = true;
        hasSeenIncidenciasPopup.value = true; // <- Marcamos como "ya mostrado"
      }
    } catch (error) {
      console.error("Error al cargar resumen de incidencias:", error);
    }
  }
});

// Actualizar markers
watch(
  () => pocStore.data,
  () => {
    updateMarkers();
  }
);

function updateMarkers() {
  if (!map.value || !clusterGroup.value) return;

  try {
    clusterGroup.value.clearLayers();
  } catch (e) {
    console.warn("Cluster group no estaba listo todavía:", e);
  }

  filteredVisibleDevices.value.forEach((device) => {
    if (device.Lat && device.Lon) {
      const marker = L.marker([device.Lat, device.Lon], {
        icon: iconosPorTipo[device.TipoDispositivo] || iconosPorTipo.Default,
      });
      marker.bindPopup(
        `<strong>${device.Apodo || "Sin nombre"}</strong><br>Tipo: ${
          device.TipoDispositivo
        }`
      );
      clusterGroup.value.addLayer(marker);
    }
  });
}

// Comprobar si el popup de incidencias ya ha sido visto
function closeIncidenciasPopup() {
  showIncidenciasPopup.value = false;
  localStorage.setItem("hasSeenIncidenciasPopup", "true");
}

// Comprobar si el popup de incidencias debe mostrarse
function goToIncidencias() {
  closeIncidenciasPopup();
  router.push({ name: "Incidencias" });
}
</script>

<style scoped>
.search-container {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
}
</style>
